import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/system";
import {
    Autocomplete,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@mui/material";
import _ from "lodash";
import moment from "moment";
import {organizationSettingsService} from "_services";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import BasicModal from "../../../common/BasicModal";
import SingleDatePicker from "../../../common/SingleDatePicker";

const COVERAGE_ORG = "Organization";
const COVERAGE_VERTICAL = "Vertical";

const ACTION_TYPES = ["ESCALATE", "NO_RESPONSE_NEEDED", "ACTIONED", "COMPANY_HIDE", "COMPANY_DELETE"];

const TAG_MATCH_OPTIONS = ["ANY", "ONLY"];

const TAG_PAGE_SIZE = 50;

const primaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    bgcolor: "#003300",
    "&:hover": {
        bgcolor: "#004d00",
    },
    "&:disabled": {
        bgcolor: "rgba(0, 51, 0, 0.35)",
        color: "#FFFFFF",
    },
};

const secondaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    color: "#0C2728",
    borderColor: "#CED4D4",
};

const fieldSize = "small";

const compactStackSx = {display: "flex", flexDirection: "column", gap: 1.25};

const compactSelectMenuProps = {
    PaperProps: {
        sx: {
            "& .MuiMenuItem-root": {
                minHeight: 30,
                py: 0.25,
                px: 1.25,
                fontSize: "0.8125rem",
                fontFamily: "LarsseitRegular",
                lineHeight: 1.2,
            },
        },
    },
};

const compactAutocompleteListboxProps = {
    sx: {
        py: 0.5,
        "& .MuiAutocomplete-option": {
            minHeight: 30,
            py: 0.25,
            px: 1.25,
            fontSize: "0.8125rem",
            fontFamily: "LarsseitRegular",
            lineHeight: 1.2,
        },
    },
};

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || _.get(error, "message") || "Something went wrong.";
    SnackBar.showMessage(message);
}

function parseTrendsOrgId(organizationTrendsId) {
    const n = Number(organizationTrendsId);
    return Number.isFinite(n) ? n : organizationTrendsId;
}

function formatTagOptionLabel(option) {
    if (option == null || option.id == null) {
        return "";
    }
    return `(${option.id}) - ${String(option.name ?? "")}`;
}

function mapTagSearchResults(data) {
    return (Array.isArray(data) ? data : [])
        .filter((t) => t != null && t.id != null)
        .map((t) => ({id: Number(t.id), name: String(t.name ?? "")}));
}

function initialFormState(organizationTrendsId, actionKind) {
    const orgId = organizationTrendsId != null && organizationTrendsId !== "" ? parseTrendsOrgId(organizationTrendsId) : "";
    const requiresActionType = actionKind === "keyword" || actionKind === "tag";
    return {
        name: "",
        coverage: COVERAGE_ORG,
        organizationId: orgId,
        verticalId: "",
        actionType: requiresActionType ? "ACTIONED" : "",
        isActive: true,
        isHideEnabled: false,
        isSkipEnabled: false,
        isSkipThankYouEnabled: false,
        isAutoReplyThankYouEnabled: false,
        autoReplyThankYouTextLines: "",
        selectedTag: null,
        messageTagMatch: "ANY",
        queryMessagesFrom: actionKind === "ml" ? moment.utc().startOf("day").toISOString() : "",
        keywordRows: [{keyword: "", description: ""}],
    };
}

export default function CreateTrendsAutoActionModal({
    open,
    onClose,
    actionKind,
    organizationTrendsId,
    onCreated,
}) {
    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(() => initialFormState(organizationTrendsId, null));
    const [tagOptions, setTagOptions] = useState([]);
    const [tagLoading, setTagLoading] = useState(false);
    const [tagLoadingMore, setTagLoadingMore] = useState(false);
    const [tagHasMore, setTagHasMore] = useState(true);
    const tagActiveQueryRef = useRef("");
    const tagListOffsetRef = useRef(0);
    const tagListGenerationRef = useRef(0);

    const title = useMemo(() => {
        switch (actionKind) {
            case "keyword":
                return "Create keyword auto action";
            case "tag":
                return "Create tag auto action";
            case "ml":
                return "Create ML auto action";
            case "companyAlreadyReplied":
                return "Create company already replied auto action";
            default:
                return "Create auto action";
        }
    }, [actionKind]);

    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setForm(initialFormState(organizationTrendsId, actionKind));
        }
    }, [open, organizationTrendsId, actionKind]);

    const updateField = useCallback((field, value) => {
        setForm((prev) => ({...prev, [field]: value}));
    }, []);

    const handleMlQueryDateChange = useCallback((m) => {
        if (!m) {
            updateField("queryMessagesFrom", "");
            return;
        }
        const iso = moment.utc([m.year(), m.month(), m.date()]).startOf("day").toISOString();
        updateField("queryMessagesFrom", iso);
    }, [updateField]);

    const handleCoverageChange = useCallback(
        (e) => {
            const value = e.target.value;
            setForm((prev) => {
                const next = {...prev, coverage: value};
                if (value === COVERAGE_ORG && organizationTrendsId != null && organizationTrendsId !== "") {
                    next.organizationId = parseTrendsOrgId(organizationTrendsId);
                }
                return next;
            });
        },
        [organizationTrendsId]
    );

    const loadTagOptions = useCallback(async (rawQuery) => {
        const q = String(rawQuery ?? "").trim();
        const myGen = ++tagListGenerationRef.current;
        tagActiveQueryRef.current = q;
        tagListOffsetRef.current = 0;
        setTagLoading(true);
        setTagLoadingMore(false);
        setTagHasMore(true);
        try {
            const params = {max: TAG_PAGE_SIZE, offset: 0};
            if (q) {
                params.query = q;
            }
            const res = await organizationSettingsService.searchTrendsTags(params);
            const mapped = mapTagSearchResults(res.data);
            if (myGen !== tagListGenerationRef.current) {
                return;
            }
            setTagOptions(mapped);
            tagListOffsetRef.current = mapped.length;
            setTagHasMore(mapped.length >= TAG_PAGE_SIZE);
        } catch (e) {
            if (myGen !== tagListGenerationRef.current) {
                return;
            }
            setTagOptions([]);
            setTagHasMore(false);
            tagListOffsetRef.current = 0;
            handleRequestError(e);
        } finally {
            if (myGen === tagListGenerationRef.current) {
                setTagLoading(false);
            }
        }
    }, []);

    const loadMoreTags = useCallback(async () => {
        if (tagLoading || tagLoadingMore || !tagHasMore) {
            return;
        }
        const listGen = tagListGenerationRef.current;
        const queryForPage = tagActiveQueryRef.current;
        const offset = tagListOffsetRef.current;
        setTagLoadingMore(true);
        try {
            const params = {max: TAG_PAGE_SIZE, offset};
            if (queryForPage) {
                params.query = queryForPage;
            }
            const res = await organizationSettingsService.searchTrendsTags(params);
            if (listGen !== tagListGenerationRef.current || queryForPage !== tagActiveQueryRef.current) {
                return;
            }
            const mapped = mapTagSearchResults(res.data);
            if (!mapped.length) {
                setTagHasMore(false);
                return;
            }
            tagListOffsetRef.current += mapped.length;
            setTagOptions((prev) => {
                const seen = new Set(prev.map((t) => t.id));
                const next = [...prev];
                for (const t of mapped) {
                    if (!seen.has(t.id)) {
                        seen.add(t.id);
                        next.push(t);
                    }
                }
                return next;
            });
            setTagHasMore(mapped.length >= TAG_PAGE_SIZE);
        } catch (e) {
            if (listGen === tagListGenerationRef.current && queryForPage === tagActiveQueryRef.current) {
                handleRequestError(e);
            }
        } finally {
            setTagLoadingMore(false);
        }
    }, [tagHasMore, tagLoading, tagLoadingMore]);

    const debouncedTagSearch = useMemo(() => _.debounce((q) => loadTagOptions(q), 300), [loadTagOptions]);

    useEffect(() => () => debouncedTagSearch.cancel(), [debouncedTagSearch]);

    useEffect(() => {
        if (!open) {
            tagListGenerationRef.current += 1;
            setTagOptions([]);
            setTagHasMore(true);
            setTagLoadingMore(false);
            tagActiveQueryRef.current = "";
            tagListOffsetRef.current = 0;
            debouncedTagSearch.cancel();
        }
    }, [open, debouncedTagSearch]);

    useEffect(() => {
        if (open && actionKind === "tag" && activeStep === 1) {
            loadTagOptions("");
        }
    }, [open, actionKind, activeStep, loadTagOptions]);

    const tagOptionsWithSelection = useMemo(() => {
        const sel = form.selectedTag;
        if (!sel?.id) {
            return tagOptions;
        }
        if (tagOptions.some((o) => o.id === sel.id)) {
            return tagOptions;
        }
        return [sel, ...tagOptions];
    }, [tagOptions, form.selectedTag]);

    const tagListboxProps = useMemo(
        () => ({
            ...compactAutocompleteListboxProps,
            onScroll: (e) => {
                const el = e.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight > 8) {
                    return;
                }
                loadMoreTags();
            },
        }),
        [loadMoreTags]
    );

    const requiresActionType = actionKind === "keyword" || actionKind === "tag";

    const validateStep0 = useCallback(() => {
        if (!String(form.name || "").trim()) {
            SnackBar.showMessage("Name is required.");
            return false;
        }
        if (form.coverage === COVERAGE_ORG) {
            const oid = form.organizationId;
            if (oid === "" || oid === null || oid === undefined) {
                SnackBar.showMessage("Organization ID is required when coverage is Organization.");
                return false;
            }
        } else if (form.coverage === COVERAGE_VERTICAL) {
            const vid = form.verticalId;
            if (vid === "" || vid === null || vid === undefined) {
                SnackBar.showMessage("Vertical ID is required when coverage is Vertical.");
                return false;
            }
        }
        if (requiresActionType && !String(form.actionType || "").trim()) {
            SnackBar.showMessage("Action type is required for this auto action type.");
            return false;
        }
        return true;
    }, [form, requiresActionType]);

    const validateStep1 = useCallback(() => {
        if (actionKind === "keyword") {
            const rows = form.keywordRows || [];
            const filled = rows.filter((r) => String(r.keyword || "").trim());
            if (!filled.length) {
                SnackBar.showMessage("Add at least one keyword.");
                return false;
            }
        }
        if (actionKind === "tag") {
            if (!form.selectedTag?.id) {
                SnackBar.showMessage("Select a tag.");
                return false;
            }
        }
        if (actionKind === "ml") {
            const q = String(form.queryMessagesFrom || "").trim();
            if (!q) {
                SnackBar.showMessage("Choose a date for query messages from.");
                return false;
            }
        }
        return true;
    }, [actionKind, form.keywordRows, form.selectedTag, form.queryMessagesFrom]);

    const buildBasePayload = useCallback(() => {
        const thankYouLines = String(form.autoReplyThankYouTextLines || "")
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean);
        const payload = {
            name: String(form.name).trim(),
            coverage: form.coverage,
            isActive: form.isActive,
            isHideEnabled: form.isHideEnabled,
            isSkipEnabled: form.isSkipEnabled,
            isSkipThankYouEnabled: form.isSkipThankYouEnabled,
            isAutoReplyThankYouEnabled: form.isAutoReplyThankYouEnabled,
        };
        if (thankYouLines.length) {
            payload.autoReplyThankYouText = thankYouLines;
        }
        if (form.coverage === COVERAGE_ORG) {
            payload.organizationId = Number(form.organizationId);
        } else {
            payload.verticalId = Number(form.verticalId);
        }
        const at = String(form.actionType || "").trim();
        if (at) {
            payload.actionType = at;
        }
        return payload;
    }, [form]);

    const handleSubmit = useCallback(async () => {
        if (!validateStep1()) {
            return;
        }
        setSubmitting(true);
        const base = buildBasePayload();
        let promise;
        try {
            if (actionKind === "keyword") {
                const keywords = (form.keywordRows || [])
                    .filter((r) => String(r.keyword || "").trim())
                    .map((r) => {
                        const desc = String(r.description || "").trim();
                        return desc ? {keyword: String(r.keyword).trim(), description: desc} : {keyword: String(r.keyword).trim()};
                    });
                promise = organizationSettingsService.createTrendsKeywordAutoAction({
                    ...base,
                    keywords,
                });
            } else if (actionKind === "tag") {
                promise = organizationSettingsService.createTrendsTagAutoAction({
                    ...base,
                    tagId: Number(form.selectedTag.id),
                    messageTagMatch: form.messageTagMatch,
                });
            } else if (actionKind === "ml") {
                promise = organizationSettingsService.createTrendsMlAutoAction({
                    ...base,
                    queryMessagesFrom: String(form.queryMessagesFrom).trim(),
                });
            } else if (actionKind === "companyAlreadyReplied") {
                promise = organizationSettingsService.createTrendsCompanyAlreadyRepliedAutoAction(base);
            } else {
                setSubmitting(false);
                return;
            }
            const res = await promise;
            if (res.status === 201) {
                SnackBar.showMessage("Auto action created.");
                onCreated?.();
                onClose();
            } else {
                SnackBar.showMessage(_.get(res, "data.message") || "Unexpected response.");
            }
        } catch (e) {
            handleRequestError(e);
        } finally {
            setSubmitting(false);
        }
    }, [actionKind, buildBasePayload, form.keywordRows, form.selectedTag, form.messageTagMatch, form.queryMessagesFrom, onClose, onCreated, validateStep1]);

    const handleNext = useCallback(() => {
        if (activeStep === 0 && !validateStep0()) {
            return;
        }
        setActiveStep(1);
    }, [activeStep, validateStep0]);

    const handleBack = useCallback(() => {
        setActiveStep(0);
    }, []);

    const addKeywordRow = useCallback(() => {
        setForm((prev) => ({
            ...prev,
            keywordRows: [...(prev.keywordRows || []), {keyword: "", description: ""}],
        }));
    }, []);

    const updateKeywordRow = useCallback((index, field, value) => {
        setForm((prev) => {
            const rows = [...(prev.keywordRows || [])];
            rows[index] = {...rows[index], [field]: value};
            return {...prev, keywordRows: rows};
        });
    }, []);

    const removeKeywordRow = useCallback((index) => {
        setForm((prev) => {
            const rows = [...(prev.keywordRows || [])];
            rows.splice(index, 1);
            return {...prev, keywordRows: rows.length ? rows : [{keyword: "", description: ""}]};
        });
    }, []);

    const closeModal = useCallback(() => {
        if (!submitting) {
            onClose();
        }
    }, [onClose, submitting]);

    const stepper = (
        <Stepper
            activeStep={activeStep}
            sx={{
                mb: 1.5,
                "& .MuiStepLabel-label": {fontSize: "0.8125rem"},
                "& .MuiStepIcon-root": {fontSize: "1.1rem"},
            }}
        >
            <Step>
                <StepLabel>Basics</StepLabel>
            </Step>
            <Step>
                <StepLabel>Details</StepLabel>
            </Step>
        </Stepper>
    );

    const basicsFields = (
        <Box sx={compactStackSx}>
            <TextField
                size={fieldSize}
                label="Name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                fullWidth
                required
            />
            <FormControl fullWidth size={fieldSize}>
                <InputLabel id="coverage-label">Coverage</InputLabel>
                <Select
                    labelId="coverage-label"
                    label="Coverage"
                    value={form.coverage}
                    onChange={handleCoverageChange}
                    MenuProps={compactSelectMenuProps}
                >
                    <MenuItem value={COVERAGE_ORG}>Organization</MenuItem>
                    <MenuItem value={COVERAGE_VERTICAL}>Vertical</MenuItem>
                </Select>
            </FormControl>
            {form.coverage === COVERAGE_ORG ? (
                <TextField
                    size={fieldSize}
                    label="Trends organization ID"
                    value={form.organizationId}
                    fullWidth
                    InputProps={{readOnly: true}}
                />
            ) : (
                <TextField
                    size={fieldSize}
                    label="Vertical ID"
                    value={form.verticalId}
                    onChange={(e) => updateField("verticalId", e.target.value)}
                    fullWidth
                    required
                />
            )}
            <FormControl fullWidth size={fieldSize}>
                <InputLabel id="action-type-label">Action type</InputLabel>
                <Select
                    labelId="action-type-label"
                    label="Action type"
                    value={form.actionType}
                    onChange={(e) => updateField("actionType", e.target.value)}
                    MenuProps={compactSelectMenuProps}
                >
                    {!requiresActionType && (
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                    )}
                    {ACTION_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                            {t}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControlLabel
                sx={{"& .MuiFormControlLabel-label": {fontSize: "0.8125rem"}}}
                control={
                    <Checkbox size="small" checked={form.isActive} onChange={(e) => updateField("isActive", e.target.checked)}/>
                }
                label="Active"
            />
            <FormControlLabel
                sx={{"& .MuiFormControlLabel-label": {fontSize: "0.8125rem"}}}
                control={
                    <Checkbox size="small" checked={form.isHideEnabled} onChange={(e) => updateField("isHideEnabled", e.target.checked)}/>
                }
                label="Hide enabled"
            />
            <FormControlLabel
                sx={{"& .MuiFormControlLabel-label": {fontSize: "0.8125rem"}}}
                control={
                    <Checkbox size="small" checked={form.isSkipEnabled} onChange={(e) => updateField("isSkipEnabled", e.target.checked)}/>
                }
                label="Skip enabled"
            />
            <FormControlLabel
                sx={{"& .MuiFormControlLabel-label": {fontSize: "0.8125rem"}}}
                control={
                    <Checkbox
                        size="small"
                        checked={form.isSkipThankYouEnabled}
                        onChange={(e) => updateField("isSkipThankYouEnabled", e.target.checked)}
                    />
                }
                label="Skip thank you enabled"
            />
            <FormControlLabel
                sx={{"& .MuiFormControlLabel-label": {fontSize: "0.8125rem"}}}
                control={
                    <Checkbox
                        size="small"
                        checked={form.isAutoReplyThankYouEnabled}
                        onChange={(e) => updateField("isAutoReplyThankYouEnabled", e.target.checked)}
                    />
                }
                label="Auto-reply thank you enabled"
            />
            <TextField
                size={fieldSize}
                label="Auto-reply thank you lines (one per line)"
                value={form.autoReplyThankYouTextLines}
                onChange={(e) => updateField("autoReplyThankYouTextLines", e.target.value)}
                fullWidth
                multiline
                minRows={1}
            />
        </Box>
    );

    const keywordDetails = (
        <Box sx={compactStackSx}>
            <Typography sx={{fontFamily: "LarsseitMedium", color: "#0C2728", fontSize: "0.875rem"}}>Keywords</Typography>
            {(form.keywordRows || []).map((row, index) => (
                <Box key={index} sx={{display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start"}}>
                    <TextField
                        size={fieldSize}
                        label="Keyword"
                        value={row.keyword}
                        onChange={(e) => updateKeywordRow(index, "keyword", e.target.value)}
                        sx={{flex: 1, minWidth: 120}}
                    />
                    <TextField
                        size={fieldSize}
                        label="Description"
                        value={row.description}
                        onChange={(e) => updateKeywordRow(index, "description", e.target.value)}
                        sx={{flex: 1, minWidth: 120}}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => removeKeywordRow(index)}
                        disabled={(form.keywordRows || []).length <= 1}
                        sx={secondaryButtonSx}
                    >
                        Remove
                    </Button>
                </Box>
            ))}
            <Button size="small" variant="outlined" onClick={addKeywordRow} sx={secondaryButtonSx}>
                Add keyword
            </Button>
        </Box>
    );

    const tagDetails = (
        <Box sx={compactStackSx}>
            <Autocomplete
                size={fieldSize}
                options={tagOptionsWithSelection}
                loading={tagLoading || tagLoadingMore}
                filterOptions={(opts) => opts}
                getOptionLabel={formatTagOptionLabel}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                value={form.selectedTag}
                onChange={(event, newValue) => {
                    updateField("selectedTag", newValue);
                }}
                onInputChange={(event, value, reason) => {
                    if (reason === "input") {
                        debouncedTagSearch(value);
                    }
                    if (reason === "clear") {
                        debouncedTagSearch.cancel();
                        updateField("selectedTag", null);
                        loadTagOptions("");
                    }
                }}
                onOpen={() => {
                    if (tagOptions.length === 0) {
                        loadTagOptions("");
                    }
                }}
                ListboxProps={tagListboxProps}
                renderInput={(params) => (
                    <TextField {...params} size={fieldSize} label="Tag" placeholder="Search by name…"/>
                )}
            />
            <FormControl fullWidth size={fieldSize}>
                <InputLabel id="tag-match-label">Message tag match</InputLabel>
                <Select
                    labelId="tag-match-label"
                    label="Message tag match"
                    value={form.messageTagMatch}
                    onChange={(e) => updateField("messageTagMatch", e.target.value)}
                    MenuProps={compactSelectMenuProps}
                >
                    {TAG_MATCH_OPTIONS.map((t) => (
                        <MenuItem key={t} value={t}>
                            {t}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );

    const mlDetails = (
        <Box sx={compactStackSx}>
            <Typography
                component="label"
                sx={{fontSize: "0.8125rem", fontFamily: "LarsseitMedium", color: "#0C2728", display: "block"}}
            >
                Query messages from
            </Typography>
            <Box sx={{maxWidth: 320}}>
                <SingleDatePicker
                    key={`ml-query-${open}-${activeStep}`}
                    withIcon
                    enabled
                    handleChange={handleMlQueryDateChange}
                    defaultDate={form.queryMessagesFrom || undefined}
                />
            </Box>
        </Box>
    );

    const companyDetails = (
        <Typography sx={{fontFamily: "LarsseitRegular", color: "#495D5E", fontSize: "0.875rem"}}>
            No extra fields. Review basics and create.
        </Typography>
    );

    const detailsContent =
        actionKind === "keyword"
            ? keywordDetails
            : actionKind === "tag"
                ? tagDetails
                : actionKind === "ml"
                    ? mlDetails
                    : companyDetails;

    return (
        <BasicModal
            open={open}
            handleClose={closeModal}
            title={title}
            style={{width: 720, maxWidth: "calc(100vw - 32px)"}}
        >
            {stepper}
            {activeStep === 0 ? basicsFields : detailsContent}
            <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1, mt: 2}}>
                {activeStep === 1 ? (
                    <Button size="small" variant="outlined" onClick={handleBack} disabled={submitting} sx={secondaryButtonSx}>
                        Back
                    </Button>
                ) : null}
                {activeStep === 0 ? (
                    <Button size="small" variant="contained" onClick={handleNext} sx={primaryButtonSx}>
                        Next
                    </Button>
                ) : (
                    <Button size="small" variant="contained" onClick={handleSubmit} disabled={submitting} sx={primaryButtonSx}>
                        {submitting ? <CircularProgress size={20} color="inherit"/> : "Create"}
                    </Button>
                )}
            </Box>
        </BasicModal>
    );
}
